import React, { useState, useEffect, useTransition } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchSalaryAdvanceRequest,
  updateSalaryAdvanceRequest,
  submitSalaryAdvance,
  clearSalaryAdvanceRequestsError,
} from "../../../../contents/redux/actions/salaryAdvanceRequestActions";
import {
  MonetizationOnRounded,
  PaymentsRounded,
  LiveHelpRounded,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { toast } from "react-toastify";
import styles from "../../../utilities/cssmodules/EditSalaryAdvance.module.css";

const EditSalaryAdvance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPending, startTransition] = useTransition();
  const { requestId } = location.state || {};
  const { salaryAdvanceDetails, errors, isLoading } = useSelector(
    (state) => state.salaryAdvance || {},
  );
  const userId = localStorage.getItem("loggedInUserId");
  const [inputs, setInputs] = useState({
    purpose: "",
    amount: "",
    currency: "",
  });
  const [formErrors, setFormErrors] = useState({
    purpose: "",
    amount: "",
    currency: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const purposes = [
    { id: "1", name: "Transportation" },
    { id: "2", name: "Internet" },
    { id: "3", name: "Lunch" },
  ];

  const currencies = [
    { id: "1", name: "USD" },
    { id: "2", name: "LRD" },
  ];

  // Fetch request details
  useEffect(() => {
    if (!requestId) {
      console.warn("Missing requestId:", { requestId });
      toast.error("Invalid request ID");
      navigate("/myrequests");
      return;
    }
    startTransition(() => {
      dispatch(fetchSalaryAdvanceRequest(requestId));
    });
  }, [requestId, dispatch, navigate]);

  // Restrict editing to saved requests
  useEffect(() => {
    if (salaryAdvanceDetails) {
      console.log("Checking status:", salaryAdvanceDetails.status);
      if (!salaryAdvanceDetails.status) {
        toast.error("Request status is undefined. Contact support.");
        navigate("/myrequests");
      } else if (salaryAdvanceDetails.status !== "Saved") {
        toast.error("Only saved requests can be edited.");
        navigate("/myrequests");
      }
    }
  }, [salaryAdvanceDetails, navigate]);

  // Populate form with fetched data
  useEffect(() => {
    if (salaryAdvanceDetails && salaryAdvanceDetails.id === requestId) {
      console.log(
        "Populating form with salaryAdvanceDetails:",
        salaryAdvanceDetails,
      );
      setInputs({
        purpose: salaryAdvanceDetails.purpose || "",
        amount: salaryAdvanceDetails.amount?.toString() || "",
        currency: salaryAdvanceDetails.currency || "",
      });
    }
  }, [salaryAdvanceDetails, requestId]);

  // Handle Redux errors
  useEffect(() => {
    if (errors.fetchSalaryAdvanceRequests) {
      toast.error(errors.fetchSalaryAdvanceRequests);
      dispatch(clearSalaryAdvanceRequestsError());
    }
    if (errors.updateSalaryAdvanceRequest) {
      toast.error(errors.updateSalaryAdvanceRequest);
      dispatch(clearSalaryAdvanceRequestsError());
      setIsSubmitting(false);
    }
  }, [
    errors.fetchSalaryAdvanceRequests,
    errors.updateSalaryAdvanceRequest,
    dispatch,
  ]);

  // Validate form inputs for isFormValid
  useEffect(() => {
    const newErrors = {
      purpose: "",
      amount: "",
      currency: "",
    };
    let isValid = true;

    if (!inputs.purpose.trim()) {
      newErrors.purpose = "Purpose is required";
      isValid = false;
    }

    const amountValue = parseFloat(inputs.amount);
    if (!inputs.amount || isNaN(amountValue) || amountValue <= 0) {
      newErrors.amount = "Please enter a valid positive amount";
      isValid = false;
    }

    if (!inputs.currency) {
      newErrors.currency = "Please select a currency";
      isValid = false;
    }

    setIsFormValid(isValid);
    // Only update formErrors on save/submit, not here, to avoid unnecessary renders
  }, [inputs]);

  // Validate form inputs for save/submit (updates formErrors)
  const validateInputs = () => {
    const newErrors = {
      purpose: "",
      amount: "",
      currency: "",
    };
    let isValid = true;

    if (!inputs.purpose.trim()) {
      newErrors.purpose = "Purpose is required";
      isValid = false;
    }

    const amountValue = parseFloat(inputs.amount);
    if (!inputs.amount || isNaN(amountValue) || amountValue <= 0) {
      newErrors.amount = "Please enter a valid positive amount";
      isValid = false;
    }

    if (!inputs.currency) {
      newErrors.currency = "Please select a currency";
      isValid = false;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  // Handle input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Go back to previous component
  const goBack = () => {
    navigate(-1);
  };

  // Handle save
  const handleSave = (e) => {
    e.preventDefault();
    if (!validateInputs()) {
      toast.error("Please fix the form errors before saving");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }
    if (!salaryAdvanceDetails?.requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }

    setIsSubmitting(true);
    const updatedData = {
      userId: parseInt(userId),
      requestTypeId: parseInt(salaryAdvanceDetails.requestTypeId),
      amount: parseFloat(inputs.amount),
      currency: inputs.currency,
      isOnSaved: true,
      purpose: inputs.purpose.trim(),
    };

    console.log("handleSave - Sending payload:", updatedData);

    startTransition(() => {
      dispatch(updateSalaryAdvanceRequest(requestId, updatedData))
        .then((response) => {
          if (!response || !response.id) {
            throw new Error(
              "Invalid response from server: Update not confirmed",
            );
          }
          toast.success("Salary Advance request saved successfully");
          navigate("/myrequests");
        })
        .catch((err) => {
          const errorMessage =
            err.response?.data?.message ||
            err.message ||
            "Failed to save salary advance request";
          console.error("Error saving salary advance request:", {
            error: err,
            response: err.response?.data,
          });
          toast.error(errorMessage);
          setIsSubmitting(false);
        });
    });
  };

  // Handle submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateInputs()) {
      toast.error("Please fix the form errors before submitting");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }
    if (!salaryAdvanceDetails?.requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }

    setIsSubmitting(true);
    const updatedData = {
      userId: parseInt(userId),
      requestTypeId: parseInt(salaryAdvanceDetails.requestTypeId),
      amount: parseFloat(inputs.amount),
      currency: inputs.currency,
      isOnSaved: false,
      purpose: inputs.purpose.trim(),
    };

    console.log("handleSubmit - Sending payload:", updatedData);

    startTransition(() => {
      dispatch(updateSalaryAdvanceRequest(requestId, updatedData))
        .then((response) => {
          if (!response || !response.id) {
            throw new Error(
              "Invalid response from server: Update not confirmed",
            );
          }
          return dispatch(submitSalaryAdvance(requestId));
        })
        .then(() => {
          toast.success("Salary Advance request submitted successfully");
          navigate("/myrequests");
        })
        .catch((err) => {
          const errorMessage =
            err.response?.data?.message ||
            err.message ||
            "Failed to submit salary advance request";
          console.error("Error submitting salary advance request:", {
            error: err,
            response: err.response?.data,
          });
          toast.error(errorMessage);
          setIsSubmitting(false);
        });
    });
  };

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["component-title"]}>
        <div className={styles["flex-row-container"]}>
          <div className={styles["flex-row-custom-1"]}>
            <span
              className={styles["title-back-button"]}
              onClick={goBack}
              aria-label="Go back to previous page"
            >
              <Tooltip title="Go Back" arrow placement="top">
                <ArrowBackIosIcon />
              </Tooltip>
            </span>
            <span>Edit Salary Advance Request</span>
          </div>
        </div>
      </div>
      <div className={styles["generic-activity-component-description"]}>
        Edit this salary advance request by updating the form below with the
        desired information and submitting when you are done. Ensure all fields
        have accurate information.
      </div>
      {isLoading && (
        <div className={styles["loading-message"]}>
          Loading request details...
        </div>
      )}
      {errors.fetchSalaryAdvanceRequests && (
        <div className={styles["error-message"]}>
          {errors.fetchSalaryAdvanceRequests}
        </div>
      )}
      {!isLoading &&
        !salaryAdvanceDetails &&
        !errors.fetchSalaryAdvanceRequests && (
          <div className={styles["error-message"]}>
            Request details not found
          </div>
        )}
      <div className={styles["generic_request_content_component_wrapper"]}>
        <form
          className={styles["generic-activity-component-form"]}
          onSubmit={handleSubmit}
          noValidate
        >
          <div className={styles["form-group"]}>
            <label className={styles["form-label"]} htmlFor="editrequestamount">
              <MonetizationOnRounded /> Amount
            </label>
            <input
              type="number"
              name="amount"
              id="editrequestamount"
              placeholder="Enter amount for request"
              required
              value={inputs.amount}
              onChange={handleChange}
              disabled={isSubmitting || isPending}
              aria-describedby="amount-error"
              aria-invalid={!!formErrors.amount}
              min="0"
              step="0.01"
              className={styles["form-input"]}
            />
            {formErrors.amount && (
              <span className={styles["error"]} id="amount-error">
                {formErrors.amount}
              </span>
            )}
          </div>

          <div className={styles["form-group"]}>
            <label
              className={styles["form-label"]}
              htmlFor="editrequestcurrency"
            >
              <PaymentsRounded /> Currency
            </label>
            <select
              name="currency"
              id="editrequestcurrency"
              required
              value={inputs.currency}
              onChange={handleChange}
              disabled={isSubmitting || isPending}
              aria-describedby="currency-error"
              aria-invalid={!!formErrors.currency}
              className={styles["form-select"]}
            >
              <option value="">Select a currency</option>
              {currencies.map((currency) => (
                <option key={currency.id} value={currency.name}>
                  {currency.name}
                </option>
              ))}
            </select>
            {formErrors.currency && (
              <span className={styles["error"]} id="currency-error">
                {formErrors.currency}
              </span>
            )}
          </div>

          <div className={styles["form-group"]}>
            <label
              className={styles["form-label"]}
              htmlFor="editrequestpurpose"
            >
              <LiveHelpRounded /> Purpose
            </label>
            <select
              name="purpose"
              id="editrequestpurpose"
              required
              value={inputs.purpose}
              onChange={handleChange}
              disabled={isSubmitting || isPending}
              aria-describedby="purpose-error"
              aria-invalid={!!formErrors.purpose}
              className={styles["form-select"]}
            >
              <option value="">Select a purpose</option>
              {purposes.map((purpose) => (
                <option key={purpose.id} value={purpose.name}>
                  {purpose.name}
                </option>
              ))}
            </select>
            {formErrors.purpose && (
              <span className={styles["error"]} id="purpose-error">
                {formErrors.purpose}
              </span>
            )}
          </div>

          <div className={styles["button-row"]}>
            <button
              type="button"
              className={styles["generic-save-btn"]}
              onClick={handleSave}
              disabled={isSubmitting || isPending || isLoading || !isFormValid}
              aria-label="Save salary advance request"
              aria-busy={isSubmitting || isPending}
            >
              {isSubmitting || isPending ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              className={styles["generic-submit-btn"]}
              onClick={handleSubmit}
              disabled={isSubmitting || isPending || isLoading || !isFormValid}
              aria-label="Submit salary advance request"
              aria-busy={isSubmitting || isPending}
            >
              {isSubmitting || isPending ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSalaryAdvance;
